import { BaseListViewStore, checkPermissions, DateTimeCellData } from 'core/stores';
import { PickingBatchViewStore } from 'modules/picking-batch-view/stores';
import { PickingBatchStore } from 'modules/picking-batch/stores';
import { localStorageHandler } from 'core/services';
import { TableFilterDateTemplate, TableFilterNumericTemplate, TableFilterTemplate } from 'themes/components';
import { observable, action, makeObservable } from 'mobx';
import {
	createFileUrl,
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	isSuperAdminOrganization,
	isWarehouse,
	urlToFile,
} from 'core/utils';
import { ApplicationService, CoreUserService, PickingBatchService } from 'common/data';
import { UsernameCellData } from 'core/stores/CellDataStore';
import { LookupStore } from 'modules/lookup/stores';
import * as printJS from 'print-js';
import _ from 'lodash';

class PickingBatchListViewStore extends BaseListViewStore {
	@observable change = false;
	hasPiiAccess = false;
	isExpand = true;
	showActions = true;
	localStorageWidthKey = 'batches-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new PickingBatchViewStore(rootStore),
			queryParams: {
				embed: [],
				filter: localStorageHandler.get('picking-batches-filter'),
			},
			findFunction: 'findPickingBatches',
			tableData: {
				columns: [
					{
						key: 'pickingBatchId',
						title: 'PICKINGBATCHLISTVIEW.PICKINGBATCHID',
						selected: true,
						width: 200,
						filterType: TableFilterNumericTemplate,
						filter: null,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'dateCreated',
						title: 'PICKINGBATCHLISTVIEW.PICKINGBATCHDATE',
						dataCell: DateTimeCellData,
						filterType: TableFilterDateTemplate,
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
					},
					{
						key: 'warehouseName',
						filterKey: 'applicationTitle',
						title: 'PICKINGBATCHLISTVIEW.PICKINGBATCHWAREHOUSE',
						service: 'applicationService',
						filterType: TableFilterTemplate,
						isWarehouseOnlyColumn: true,
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'userName',
						title: 'PICKINGBATCHLISTVIEW.PICKINGBATCHUSER',
						service: 'coreUserService',
						dataCell: UsernameCellData,
						filterType: TableFilterTemplate,
						selected: true,
						width: 200,
						filter: null,
						frozen: false,
						sortable: true,
					},
					{
						key: 'quantity',
						title: 'PICKINGBATCHLISTVIEW.PICKINGBATCHQUANTITY',
						selected: true,
						filterType: TableFilterNumericTemplate,
						width: 200,
						filter: null,
						sortable: true,
					},
				],
				actions: {
					onPicklistPrint: item => this.printPicklist(item.id),
					onExportToCSV: item => this.exportToCSV(item.id),
					onReprintLabels: item => this.reprintLabels(item.id),
				},
				sortObject: [
					{
						field: 'pickingBatchId',
						dir: 'asc',
					},
				],
			},
			routes: {
				list: () => this.rootStore.routerStore.goTo('master.app.main.picking-batch.list', {}),
			},
			permissions: {
				update: () => this.authStore.hasPermission('warehouseSection.update'),
				create: () => this.authStore.hasPermission('warehouseSection.create'),
				remove: () => this.authStore.hasPermission('warehouseSection.delete'),
				read: () => this.authStore.hasPermission('warehouseSection.read'),
				full: () => this.authStore.hasPermission('warehouseSection.full'),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(this.rootStore) || isWarehouse(rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findPickingBatches);
		checkForColumnFilter(localStorageHandler.get('picking-batches-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'picking-batches-table-data';
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.showActions = checkPermissions(this).actionsAllowed;
		this.pickingBatchStore = new PickingBatchStore(rootStore);
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				new PickingBatchService(this.rootStore.app.baasic.apiClient),
				new ApplicationService(this.rootStore.app.baasic.apiClient),
				new CoreUserService(this.rootStore.app.baasic.apiClient),
			],
		});
		this.setSearchVisible();
		this.setHeaderModes();
	}

	@action.bound
	setAsChanged = () => {
		this.change = !this.change;
	};

	@action.bound
	onExpandChange = event => {
		const data = this.listStore.data;
		const item = _.find(this.listStore.data, ({ id }) => id === event.dataItem.id);
		item.expanded = !item.expanded;
		this.listStore.setResponseItem(data);
		this.setAsChanged();
	};

	@action.bound
	async findPickingBatches() {
		this.suspend();
		this.queryUtility.setFilterParams(this, { includePII: true }, this.tableFilter);
		await this.listStore.find(this.queryUtility.filter);
		_.forEach(this.listStore.response.item, item => {
			if (item.userName) {
				item.userName = item.userName.split('-')[0];
			}
		});
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'picking-batches-filter', 'picking-batches-table-data');
		this.setIsPrintable();
		this.resume();
	}

	@action.bound
	setIsPrintable = () => {
		const newData = [];
		_.forEach(this.listStore.data, data => {
			data.isPrintable = data.isLabelPrintable ? true : false;
			newData.push(data);
		});
		this.listStore.setResponseItem(newData);
	};

	@action.bound
	printPicklist = async id => {
		await this.pickingBatchStore.generatePdf(id);
		let pdfResponse = this.pickingBatchStore.response;
		if (pdfResponse.pdfData) {
			const fileURL = createFileUrl(pdfResponse.pdfData);
			printJS(fileURL);
		} else {
			this.rootStore.notificationStore.error(['PRINTPICKLISTNOTEXIST']);
		}
		this.findPickingBatches();
	};

	@action.bound
	reprintLabels = async id => {
		try {
			const pdfData = await this.pickingBatchStore.reprintLabels(id);
			const fileURL = createFileUrl(pdfData);
			printJS(fileURL);
		} catch (err) {
			this.rootStore.notificationStore.error('PICKINGBATCHLISTVIEW.NOLABELFOUND');
		}
	};

	@action.bound
	exportToCSV = async id => {
		const workbook = await this.pickingBatchStore.exportPickingBatch(id);

		//response returns base64 string and filename divided by char - so splitting it now to use separately.
		const splitResult = workbook.split('-');
		const base64String =
			'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + splitResult[0];
		urlToFile(
			base64String,
			splitResult[1] + '.xlsx',
			'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		).then(file => {
			saveAs(file);
		});
		if (workbook != null) {
			this.rootStore.notificationStore.success('EXPORTTOXLS.SUCCESS');
		} else {
			this.rootStore.notificationStore.error(['EXPORTTOXLS.ERROR']);
		}
	};

	styleCells = sheet => {
		let fontStyle = {
			bold: true,
		};
		var blueColumnFieldStyle = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: '8DB4E2' },
			font: { bold: true },
		};

		var beigeColumnFieldStyle = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'DDD9C4' },
		};

		let borderStyle = {
			top: { style: 'thin' },
			left: { style: 'thin' },
			bottom: { style: 'thin' },
			right: { style: 'thin' },
		};

		sheet.getCell('A1').fill = blueColumnFieldStyle;
		sheet.getCell('B1').fill = blueColumnFieldStyle;
		sheet.getCell('C1').fill = beigeColumnFieldStyle;
		sheet.getCell('D1').fill = blueColumnFieldStyle;
		sheet.getCell('E1').fill = beigeColumnFieldStyle;
		sheet.getCell('F1').fill = beigeColumnFieldStyle;
		sheet.getCell('G1').fill = blueColumnFieldStyle;
		sheet.getCell('H1').fill = beigeColumnFieldStyle;
		sheet.getCell('I1').fill = beigeColumnFieldStyle;
		sheet.getCell('J1').fill = blueColumnFieldStyle;
		sheet.getCell('K1').fill = blueColumnFieldStyle;
		sheet.getCell('L1').fill = beigeColumnFieldStyle;
		sheet.getCell('M1').fill = blueColumnFieldStyle;
		sheet.getCell('N1').fill = beigeColumnFieldStyle;
		sheet.getCell('O1').fill = blueColumnFieldStyle;
		sheet.getCell('P1').fill = blueColumnFieldStyle;
		sheet.getCell('Q1').fill = beigeColumnFieldStyle;
		sheet.getCell('R1').fill = blueColumnFieldStyle;
		sheet.getCell('S1').fill = blueColumnFieldStyle;
		sheet.getCell('T1').fill = blueColumnFieldStyle;
		sheet.getCell('U1').fill = blueColumnFieldStyle;
		sheet.getCell('V1').fill = beigeColumnFieldStyle;
		sheet.getCell('W1').fill = beigeColumnFieldStyle;
		sheet.getCell('X1').fill = beigeColumnFieldStyle;
		sheet.getCell('Y1').fill = beigeColumnFieldStyle;
		sheet.getCell('Z1').fill = blueColumnFieldStyle;
		sheet.getCell('AA1').fill = blueColumnFieldStyle;
		sheet.getCell('AB1').fill = beigeColumnFieldStyle;
		sheet.getCell('AC1').fill = beigeColumnFieldStyle;
		sheet.getCell('AD1').fill = beigeColumnFieldStyle;

		sheet.getRow(1).border = borderStyle;
		sheet.getRow(1).font = fontStyle;
	};
}

export default PickingBatchListViewStore;



// WEBPACK FOOTER //
// ./src/modules/picking-batch/stores/PickingBatchListViewStore.js